import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog as MuiDialog,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DialogProps } from "@mui/material/Dialog/Dialog";
import {
  FC,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";

type TDialog = {
  isOpen?: boolean;
  title?: string;
  text?: string;
  textSuffix?: string;
  fixedContent?: ReactNode;
  hideCloseButton?: boolean;
  hideBottomActions?: boolean;
  backgroundColor?: THypeColors;
  closeLabel?: string;
  onClose?: () => void;
} & PropsWithChildren &
  Omit<DialogProps, "open">;

/**
 * @desc Render a Dialog
 *
 * @param isOpen determines whether the modal is open or closed
 * @param title modal title
 * @param text modal content of modal (also html)
 * @param textSuffix Text to be added at the end of the text field.
 * @param fixedContent custom component to be used as fixed (not scrollabale) below the modale title.
 * @param hideCloseButton show/hide default close buttons
 * @param hideBottomActions show/hide bottom buttons action
 * @param closeLabel override default close label
 * @param onClose override default onClose handler
 *
 * @example Simple Dialog
 * <Dialog
 *    isOpen
 *    title="title"
 *    text="text"
 *    hideCloseButton={false}
 *    closeLabel="ok, close it!"
 *    onClose={() => doSomething()} />
 * @example Dialog with children content
 * <Dialog
 *    isOpen
 *    hideCloseButton
 *    onClose={() => doSomething()}>
 *      ...your content
 *    </Dialog>
 */

export const Dialog: FC<TDialog> = ({
  isOpen = false,
  title,
  text,
  textSuffix,
  fixedContent,
  hideCloseButton,
  hideBottomActions,
  closeLabel,
  backgroundColor,
  children,
  onClose,
  PaperProps,
  ...rest
}) => {
  const t = useIntl();
  const [open, setOpen] = useState(isOpen);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const handleClose = () => {
    setOpen(false);
  };
  const showDefaultBottomActions = useMemo(() => {
    return !hideCloseButton && !hideBottomActions;
  }, [hideBottomActions, hideCloseButton]);

  useEffect(() => setOpen(isOpen), [isOpen]);

  if (!children && !text) return null;
  return (
    <MuiDialog
      data-testid="dialog"
      open={open}
      fullScreen={fullScreen}
      maxWidth="md"
      PaperProps={{
        ...PaperProps,
      }}
      {...rest}
    >
      {!hideCloseButton && (
        <IconButton
          aria-label="close"
          role="button"
          onClick={onClose ?? handleClose}
          sx={{
            width: 40,
            height: 40,
            position: "absolute",
            top: fullScreen ? "20px" : "28px",
            right: fullScreen ? "unset" : "24px",
            left: fullScreen ? "12px" : "unset",
            fontSize: 28,
            color: theme.palette.typography.body,
          }}
        >
          <span>&times;</span>
        </IconButton>
      )}
      {title && (
        <DialogTitle>
          <span dangerouslySetInnerHTML={{ __html: title }} />
        </DialogTitle>
      )}
      {fixedContent && <Box sx={{ px: 6 }}>{fixedContent}</Box>}
      <DialogContent
        sx={
          showDefaultBottomActions
            ? {
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }
            : { display: "flex" }
        }
      >
        <Stack className="test" flex="1 1 auto">
          {text ? (
            <>
              <Typography
                variant="body"
                dangerouslySetInnerHTML={{ __html: text }}
              />
              {textSuffix && (
                <Typography
                  variant="body"
                  sx={{ color: "secondary.dark" }}
                  dangerouslySetInnerHTML={{ __html: textSuffix }}
                />
              )}
            </>
          ) : (
            children
          )}
        </Stack>
        {showDefaultBottomActions && (
          <Button
            variant="primary"
            onClick={onClose ?? handleClose}
            sx={{
              alignSelf: "center",
              minWidth: 294,
              width: fullScreen ? "100%" : "auto",
              mb: 5,
              mt: 9,
            }}
          >
            {closeLabel ?? t.formatMessage({ id: "dialogComponent.close" })}
          </Button>
        )}
      </DialogContent>
    </MuiDialog>
  );
};
